import {
  USER_ROLE_ID_MAP,
  USER_ROLES_MAP,
  USER_ROLE_IDS_CAN_SIGN_ALL_DCF_ROLES,
} from 'utils/constants'

/** Letter-size DCF template (matches database-service asset). */
export const DCF_PDF_PAGE_WIDTH = 612
export const DCF_PDF_PAGE_HEIGHT = 792

/** PDF signature blocks on the DCF form (cycle order for Root / DCH Admin). */
export const DCF_SIGNATURE_ROLE_OPTIONS = [
  { role: 'site_approval', label: 'Site Approval', requiresDecision: true },
  { role: 'dch_approval', label: 'Datacubed Health Approval', requiresDecision: false },
  { role: 'reviewed_by', label: 'Reviewed By', requiresDecision: false },
  { role: 'verified_by', label: 'Verified By', requiresDecision: false },
]

/**
 * AcroForm checkbox field names on the DCF template, keyed by decision value.
 * Must match database-service `_checkbox_on` in app/services/dcf_pdf.py.
 */
export const DCF_DECISION_CHECKBOX_FIELDS = {
  approved: 'Approved',
  rejected: 'Rejected',
}

/**
 * Widget rects on page 3 of app/assets/data_correction_form.pdf (PDF points,
 * origin bottom-left). Drawn on top of the AcroForm so viewers that skip
 * form appearances still show the decision.
 */
export const DCF_DECISION_CHECKBOX_RECTS = {
  approved: { page: 3, x: 176.728, y: 614.29, width: 18, height: 18 },
  rejected: { page: 3, x: 285.383, y: 613.636, width: 18, height: 18 },
}

/**
 * Default one-role mapping (used when study users return after prod Go).
 * Root / DCH Admin use one-click cycle across all unsigned roles instead.
 */
export const ADMIN_ROLE_ID_TO_DCF_SIGNATURE_ROLE = {
  [USER_ROLE_ID_MAP.siteCoordinator]: 'site_approval',
  [USER_ROLE_ID_MAP.siteCoordinatorUAT]: 'site_approval',
  [USER_ROLE_ID_MAP.siteCoordinatorDEV]: 'site_approval',
  [USER_ROLE_ID_MAP.investigator]: 'dch_approval', // Study Admin (id 4)
  [USER_ROLE_ID_MAP.dchAdmin]: 'reviewed_by',
  [USER_ROLE_ID_MAP.root]: 'verified_by',
}

const ADMIN_ROLE_NAME_TO_DCF_SIGNATURE_ROLE = {
  [USER_ROLES_MAP.root.toLowerCase()]: 'verified_by',
  [USER_ROLES_MAP.dchAdmin.toLowerCase()]: 'reviewed_by',
  [USER_ROLES_MAP.studyAdmin.toLowerCase()]: 'dch_approval',
  [USER_ROLES_MAP.investigator.toLowerCase()]: 'dch_approval',
  [USER_ROLES_MAP.siteCoordinator.toLowerCase()]: 'site_approval',
  [USER_ROLES_MAP.siteCoordinatorUAT.toLowerCase()]: 'site_approval',
  [USER_ROLES_MAP.siteCoordinatorDEV.toLowerCase()]: 'site_approval',
}

const toTopOriginPct = ({ x, y, width, height }) => {
  const xMinPct = (x / DCF_PDF_PAGE_WIDTH) * 100
  const xMaxPct = ((x + width) / DCF_PDF_PAGE_WIDTH) * 100
  const yMinPct = ((DCF_PDF_PAGE_HEIGHT - (y + height)) / DCF_PDF_PAGE_HEIGHT) * 100
  const yMaxPct = ((DCF_PDF_PAGE_HEIGHT - y) / DCF_PDF_PAGE_HEIGHT) * 100
  return { xMinPct, xMaxPct, yMinPct, yMaxPct }
}

export const DCF_SIGNATURE_PLACEMENTS = {
  site_approval: {
    page: 3,
    hotspotPdf: { x: 110, y: 470, width: 430, height: 90 },
    stampPdf: { x: 300, y: 485, width: 220, height: 55 },
  },
  dch_approval: {
    page: 3,
    hotspotPdf: { x: 110, y: 300, width: 430, height: 90 },
    stampPdf: { x: 300, y: 315, width: 220, height: 55 },
  },
  reviewed_by: {
    page: 4,
    hotspotPdf: { x: 110, y: 300, width: 430, height: 90 },
    stampPdf: { x: 300, y: 315, width: 220, height: 55 },
  },
  verified_by: {
    page: 4,
    hotspotPdf: { x: 100, y: 135, width: 440, height: 90 },
    stampPdf: { x: 300, y: 150, width: 220, height: 55 },
  },
}

Object.keys(DCF_SIGNATURE_PLACEMENTS).forEach(role => {
  const placement = DCF_SIGNATURE_PLACEMENTS[role]
  placement.hotspot = toTopOriginPct(placement.hotspotPdf)
})

export const getDcfSignatureRoleConfig = role =>
  DCF_SIGNATURE_ROLE_OPTIONS.find(item => item.role === role) || null

export const getDcfSignaturePlacement = role => DCF_SIGNATURE_PLACEMENTS[role] || null

export const isClickInDcfSignatureHotspot = (role, { page, xPct, yPct }) => {
  const placement = getDcfSignaturePlacement(role)
  if (!placement || page !== placement.page) return false
  const { xMinPct, xMaxPct, yMinPct, yMaxPct } = placement.hotspot
  return xPct >= xMinPct && xPct <= xMaxPct && yPct >= yMinPct && yPct <= yMaxPct
}

export const userCanSignAllDcfRoles = (user, studyID) => {
  const roleId = user?.permissions?.[studyID]?.role_id
  return USER_ROLE_IDS_CAN_SIGN_ALL_DCF_ROLES.includes(roleId)
}

/**
 * Next unsigned stamp role for this user.
 * Root / DCH Admin cycle through all four; others use their single mapped role.
 *
 * @returns {{ role: string, label: string, requiresDecision: boolean }|null}
 */
export const resolveNextDcfSignatureRoleForUser = (user, studyID, savedRoles = []) => {
  const saved = new Set(savedRoles || [])

  if (userCanSignAllDcfRoles(user, studyID)) {
    const next = DCF_SIGNATURE_ROLE_OPTIONS.find(item => !saved.has(item.role))
    return next || null
  }

  const mapped = resolveDcfSignatureRoleForUser(user, studyID)
  if (!mapped) return null
  if (saved.has(mapped.role)) return null
  return mapped
}

/**
 * Resolve the single PDF stamp role for the current user on a study.
 * Prefer resolveNextDcfSignatureRoleForUser for the signing UI.
 */
export const resolveDcfSignatureRoleForUser = (user, studyID) => {
  const permission = user?.permissions?.[studyID]
  const roleId = permission?.role_id
  let stampRole = null

  if (roleId != null && ADMIN_ROLE_ID_TO_DCF_SIGNATURE_ROLE[roleId]) {
    stampRole = ADMIN_ROLE_ID_TO_DCF_SIGNATURE_ROLE[roleId]
  } else if (permission?.name) {
    stampRole = ADMIN_ROLE_NAME_TO_DCF_SIGNATURE_ROLE[String(permission.name).toLowerCase()] || null
  }

  if (!stampRole) return null
  return getDcfSignatureRoleConfig(stampRole)
}

export const getDcfSignerPrefillFromUser = user => {
  const signerName = (user?.nickname || user?.name || '').trim()
  const signerEmail = (user?.username || user?.email || '').trim()
  const signerTitle = (user?.title || user?.job_title || user?.jobTitle || '').trim()
  return { signerName, signerEmail, signerTitle }
}

export default {
  DCF_SIGNATURE_ROLE_OPTIONS,
  DCF_SIGNATURE_PLACEMENTS,
  DCF_DECISION_CHECKBOX_FIELDS,
  DCF_DECISION_CHECKBOX_RECTS,
  resolveDcfSignatureRoleForUser,
  resolveNextDcfSignatureRoleForUser,
  getDcfSignerPrefillFromUser,
  isClickInDcfSignatureHotspot,
  userCanSignAllDcfRoles,
}
